import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons'
import {
  Box,
  Button,
  Flex,
  FormLabel,
  HStack,
  IconButton,
  Image,
  Input,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  Text,
  Textarea
} from '@chakra-ui/react'
import { useEffect, useState } from 'react'
import styled from 'styled-components'

import { IBannerPageCardProps } from '../../../interfaces/Website/Banners/IBanners'
import { getQueryParamsManually } from './helpers'

const Error = styled(Text)`
  color: red.500;
  font-size: 0.8rem;
`

const BannerPageCard: React.FC<IBannerPageCardProps> = ({
  data,
  onChange,
  errors,
  changePosition,
  openDeleteDialog,
  handleSwitch
}) => {
  const [banner, setBanner] = useState(data)

  useEffect(() => {
    setBanner(data)
    setMediaType(data.video_url ? 'video' : 'image')
    if (data.image instanceof File) {
      setImageUrl(URL.createObjectURL(data.image))
    } else {
      setImageUrl(data.image || '')
    }
  }, [data])

  useEffect(() => {
    onChange(banner)
  }, [banner])

  const updateField = (field: string, value: any) => {
    setBanner({ ...banner, [field]: value })
  }

  const [imageUrl, setImageUrl] = useState<string>(data.image instanceof File ? '' : data.image || '')
  const [mediaType, setMediaType] = useState<string>(data.video_url ? 'video' : 'image')

  function isYouTubeUrl(url: string): boolean {
    const youtubeRegex = /^(?:https?:\/\/)?(?:www\.)?(?:m\.)?(?:youtube\.com|youtu\.be|youtube-nocookie\.com).*?$/
    return youtubeRegex.test(url)
  }

  function getYouTubeVideoId(url: string): string | null {
    const params = getQueryParamsManually(url)

    return params.v || null
  }

  function isVimeoUrl(url: string): boolean {
    const vimeoRegex =
      /^(?:https?:\/\/)?(?:www\.)?(?:player\.)?vimeo\.com\/(?:channels\/(?:\w+\/)?|groups\/(?:\w+\/)?videos\/|video\/)?(\d+)(?:[?&].*)?$/
    return vimeoRegex.test(url)
  }

  function getVimeoVideoId(url: string): string | null {
    const vimeoRegex =
      /^(?:https?:\/\/)?(?:www\.)?(?:player\.)?vimeo\.com\/(?:channels\/(?:\w+\/)?|groups\/(?:\w+\/)?videos\/|video\/)?(\d+)(?:[?&].*)?$/
    const match = url.match(vimeoRegex)
    // O ID do vídeo é capturado no primeiro grupo (match[1])
    return match && match[1] ? match[1] : null
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      updateField('image', file)
      setImageUrl(URL.createObjectURL(file))
    }
  }

  return (
    <Flex
      bg={!banner.is_active ? 'gray.50' : 'white'}
      borderRadius="md"
      p={5}
      borderWidth="2px"
      borderColor="gray.200"
      gap={4}
      flexWrap="wrap"
      shadow="md"
      _hover={{
        shadow: 'lg',
        transitionDuration: '0.3s'
      }}
    >
      <Stack flex={2}>
        <Box>
          <FormLabel fontSize="sm">Título</FormLabel>
          <Input
            size="md"
            bg="whiteAlpha"
            color="gray.900"
            borderWidth="1px"
            borderColor="gray.300"
            borderRadius="md"
            value={banner.title}
            onChange={(e) => updateField('title', e.target.value)}
          />
          {errors?.title && <Error color="red.500">{errors.title[0]}</Error>}
        </Box>
        <Box>
          <FormLabel fontSize="sm">Subtítulo</FormLabel>
          <Input
            size="md"
            bg="whiteAlpha"
            color="gray.900"
            borderWidth="1px"
            borderColor="gray.300"
            borderRadius="md"
            value={banner.subtitle}
            onChange={(e) => updateField('subtitle', e.target.value)}
          />
          {errors?.subtitle && <Error color="red.500">{errors.subtitle[0]}</Error>}
        </Box>
        <Box>
          <FormLabel fontSize="sm">Texto do banner</FormLabel>
          <Textarea
            size="md"
            bg="whiteAlpha"
            color="gray.900"
            borderWidth="1px"
            borderColor="gray.300"
            borderRadius="md"
            minH="100px"
            resize="none"
            overflowY="scroll"
            sx={{
              '&::-webkit-scrollbar': { display: 'none' },
              scrollbarWidth: 'none'
            }}
            value={banner.text}
            onChange={(e) => updateField('text', e.target.value)}
          />
        </Box>
        <Box>
          <FormLabel fontSize="sm">Texto do CTA</FormLabel>
          <Input
            size="md"
            bg="whiteAlpha"
            color="gray.900"
            borderWidth="1px"
            borderColor="gray.300"
            borderRadius="md"
            value={banner.cta_text}
            onChange={(e) => updateField('cta_text', e.target.value)}
          />
        </Box>
        <Box>
          <FormLabel fontSize="sm">Link do CTA</FormLabel>
          <Input
            size="md"
            bg="whiteAlpha"
            color="gray.900"
            borderWidth="1px"
            borderColor="gray.300"
            borderRadius="md"
            value={banner.cta_link}
            onChange={(e) => updateField('cta_link', e.target.value)}
          />
          {errors?.cta_link && <Error color="red.500">{errors.cta_link[0]}</Error>}
        </Box>
      </Stack>

      {/* image/video select */}
      <Stack flex={1} spacing={4}>
        <RadioGroup onChange={setMediaType} value={mediaType} colorScheme="gray">
          <Stack direction="row" color="gray.700">
            <Radio value="image">Imagem</Radio>
            <Radio value="video">Vídeo</Radio>
          </Stack>
        </RadioGroup>

        {mediaType === 'image' ? (
          <>
            <Input type="file" p={1} accept="image/*" onChange={handleFileChange} />
            <Box bg="gray.50">
              {imageUrl ? (
                <Image
                  src={imageUrl}
                  alt="Preview"
                  maxH="200px"
                  width="100%"
                  objectFit="cover"
                  borderRadius="md"
                  border="1px solid"
                  borderColor="gray.400"
                />
              ) : (
                <Text fontSize="sm" color="gray.700" p={2} textAlign="center">
                  Nenhuma imagem selecionada.
                </Text>
              )}
            </Box>
          </>
        ) : (
          <>
            <Input
              placeholder="Insira a URL do vídeo"
              value={banner.video_url}
              onChange={(e) => updateField('video_url', e.target.value)}
              type="url"
              color="gray.900"
              border="1px solid"
              borderColor="gray.300"
              borderRadius="md"
              px={4}
              py={2}
              _hover={{
                borderColor: 'gray.500',
                '&::placeholder': {
                  color: 'gray.700'
                }
              }}
              _focus={{
                borderColor: 'gray.600',
                boxShadow: '0 0 0 2px rgba(0, 0, 0, 0.1)'
              }}
              _placeholder={{ color: 'gray.500' }}
            />

            {banner.video_url && (
              <>
                {isVimeoUrl(banner.video_url) && (
                  <Box mt={3} borderRadius="md" overflow="hidden">
                    <iframe
                      src={`https://player.vimeo.com/video/${getVimeoVideoId(banner.video_url)}`}
                      title="Vídeo Preview"
                      width="100%"
                      height="200"
                      allow="autoplay; fullscreen"
                      style={{
                        border: '1px solid #ccc',
                        borderRadius: '8px'
                      }}
                    />
                  </Box>
                )}
                {isYouTubeUrl(banner.video_url) && (
                  <Box mt={3} borderRadius="md" overflow="hidden">
                    <iframe
                      width="100%"
                      height="200"
                      src={`https://www.youtube.com/embed/${getYouTubeVideoId(banner.video_url)}`}
                      title="YouTube video player"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    ></iframe>
                  </Box>
                )}
              </>
            )}
          </>
        )}

        <HStack spacing={4} justify="end">
          <HStack>
            <IconButton
              icon={<ChevronUpIcon />}
              aria-label="Mover para cima"
              title="Mover para cima"
              colorScheme="gray"
              size="sm"
              onClick={() => changePosition('up')}
            />
            <IconButton
              icon={<ChevronDownIcon />}
              aria-label="Mover para baixo"
              title="Mover para baixo"
              colorScheme="gray"
              size="sm"
              onClick={() => changePosition('down')}
            />
          </HStack>
          <Switch
            colorScheme="yellow"
            title={banner.is_active ? 'Desativar Banner' : 'Ativar Banner'}
            isChecked={!!data.is_active}
            hidden={!data.id}
            onChange={(e) => handleSwitch(e, data.id)}
          />
          <Button colorScheme="red" size="sm" onClick={openDeleteDialog}>
            Excluir
          </Button>
        </HStack>
      </Stack>
    </Flex>
  )
}

export default BannerPageCard
